import { cloneElement, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, FileDown } from "lucide-react";
import { useBien, useUpdateBien, useBiens, useMarkBienAsPublished } from "@/hooks/useBiens";
import { usePortalPublishMutation } from "@/hooks/usePortalPublications";
import { portalActionForStatutChange } from "@/lib/portalPublish";
import { sousOptionEnterPatch, sousOptionExitPatch } from "@/lib/sousOption";
import { todayIso } from "@/lib/today";
import { externalEnabledPortals } from "@/lib/portals";
import { useToast } from "@/components/ui/Toast";
import {
  PublicationPublishButton,
  PublicationStaleBanner,
} from "@/components/biens/PublicationGlobalStatus";
import { ImmowebStatusLine } from "@/components/biens/PortalStatusIndicator";
import { isPublishable, PUBLISH_GATE_LABEL_KEYS, type PublishGateInput } from "@/lib/publishGate";
import type { Bien } from "@/types/bien";
import { useRole } from "@/contexts/RoleContext";
import { useProfiles } from "@/hooks/useProfiles";
import { useQuartiers } from "@/hooks/useQuartiers";
import { useBiensPermRole } from "@/hooks/useBiensPermRole";
import { useFieldPermissions } from "@/hooks/useFieldPermissions";
import { useFieldPermsEditor } from "@/contexts/fieldPermsEditor";
import { PermStateCell } from "@/components/biens/PermStateCell";
import { useListNavigation } from "@/hooks/useListNavigation";
import { useBreadcrumbDetail } from "@/components/layout/BreadcrumbContext";
import { BienUpdateSchema } from "@/schemas/bien";
import type { Proximite, SurfaceOrientation } from "@/types/bien";
import { FormField } from "@/components/ui/FormField";
import { SelectField } from "@/components/ui/SelectField";
import { TextAreaField } from "@/components/ui/TextAreaField";
import { DateField } from "@/components/ui/DateField";
import { FreeTextAutocompleteField } from "@/components/ui/FreeTextAutocompleteField";
import { Checkbox } from "@/components/ui/Checkbox";
import { Badge } from "@/components/ui/Badge";
import { PhotoGallery } from "@/components/biens/PhotoGallery";
import { PebPdfDropzone } from "@/components/biens/PebPdfDropzone";
import type { BienPhoto } from "@/types/bien";
import {
  VENTE_DETAILS_LANGS,
  EQUIPEMENT_TAGS,
  VENTE_TITLE_MAX,
  VENTE_DESCRIPTION_MAX,
  PROXIMITE_POIS,
  getVenteOptions,
  getVenteFormFields,
  getVenteMissingHints,
  suggestPrixM2,
  venteDispoDatePatch,
} from "@/v2/lib/venteFormFields";
import type { VenteFieldDef, VenteSection } from "@/v2/lib/venteFormFields";
import {
  getCityFromPostalCode,
  getPostalCodeFromCity,
  getRegionFromPostalCode,
} from "@/lib/postalCodeLookup";
import { cn } from "@/lib/utils";
import { SectionCard } from "@/components/layout/SectionCard";
import { FieldGroup, type FieldGroupUnit } from "@/components/biens/FieldGroup";
import { SurfaceOrientationRepeater } from "@/components/biens/SurfaceOrientationRepeater";
import { FicheTabBar } from "@/components/biens/FicheTabBar";
import {
  VENTE_TAB_IDS,
  VENTE_TAB_META,
  VENTE_TAB_OF_SECTION,
  venteTabAlerts,
  type VenteTabId,
} from "@/v2/lib/venteTabs";
import {
  VENTE_DEFAULT_SPAN,
  VENTE_FICHE_HEADER_FIELDS,
  VENTE_FICHE_PARKED,
  VENTE_FICHE_SECTION_ORDER,
  VENTE_SECTION_ICON,
  VENTE_SECTION_LAYOUT,
  venteLayoutKeys,
} from "@/v2/lib/venteFicheLayout";
import { buildRepeaterPatch } from "@/lib/surfaceOrientationRows";

/**
 * Grille de 12 colonnes des sections — portage du traitement de la fiche
 * location (cf. v2/lib/venteFicheLayout pour les trois décisions de mise en
 * page). Classes écrites en toutes lettres : Tailwind scanne le source, une
 * classe construite (`col-span-${n}`) ne serait jamais générée.
 */
const GRID12 = "grid grid-cols-12 gap-x-4 gap-y-5";
const SPAN_CLASS: Record<number, string> = {
  1: "col-span-4 md:col-span-1",
  2: "col-span-6 md:col-span-2",
  3: "col-span-6 md:col-span-3",
  4: "col-span-12 md:col-span-4",
  5: "col-span-12 md:col-span-5",
  6: "col-span-12 md:col-span-6",
  7: "col-span-12 md:col-span-7",
  8: "col-span-12 md:col-span-8",
  9: "col-span-12 md:col-span-9",
  10: "col-span-12 md:col-span-10",
  11: "col-span-12 md:col-span-11",
  12: "col-span-12",
};

type Draft = Record<string, unknown>;

/**
 * Fiche d'ÉDITION d'un bien VENTE (montée par V2BienDetailWrapper quand
 * isForSale(bien.transaction) — la fiche V1 BienDetailPage reste la fiche
 * LOCATION, intouchée).
 *
 * Mêmes sections/champs que le formulaire de création (config partagée
 * src/v2/lib/venteFormFields.ts). Autosave au blur via useUpdateBien.
 *
 * Permissions field-level : canSee → champ absent ; canEdit=false → rendu
 * verrouillé sur place, même géométrie (même pattern F() que BienDetailPage V1).
 */
interface BienVenteDetailPageProps {
  /**
   * Bien à afficher, quand la fiche n'est pas montée sur sa propre route.
   *
   * Sert à la page « Droits vente », qui monte cette fiche hors de
   * `/v2/biens/:id` pour en réutiliser la structure. Même contrat que
   * `BienDetailPage.bienIdOverride`.
   */
  bienIdOverride?: string;
}

export function BienVenteDetailPage({ bienIdOverride }: BienVenteDetailPageProps = {}) {
  const { t, i18n } = useTranslation();
  const params = useParams<{ id: string }>();
  const id = bienIdOverride ?? params.id;
  const navigate = useNavigate();
  const permRole = useBiensPermRole();
  const { data: bien } = useBien(id, permRole);
  const { canSee, canEdit } = useFieldPermissions(permRole);
  const updateBien = useUpdateBien(id ?? "", permRole);

  // ── Publication (parité avec la fiche location) ───────────────────────────
  // Avant (audit 2026-08-07) : publier un bien vente consistait à basculer le
  // déroulant `statutPublication` sur « Publié » sans aucun garde-fou côté
  // client. Si un critère manquait, le trigger 082 refusait l'écriture et
  // l'échec était SILENCIEUX (aucun onError nulle part). L'équipe Sales
  // n'avait ni bouton « Publier » ni message d'erreur.
  const toast = useToast();
  const { role } = useRole();
  const { mutate: markAsPublished, isPending: isMarkingPublished } =
    useMarkBienAsPublished(id ?? "");
  // Pousse aussi vers les portails externes activés (Immoweb — spotto/zimmo
  // pas encore, cf. `PORTALS.enabled`). Appelé depuis `handlePublishClick`
  // plus bas. Doit rester AVANT le early return (règle des Hooks). Déclaré ici
  // (plutôt qu'à côté de `handlePublishClick` plus bas, comme avant) : l'auto-
  // save débouncée juste en dessous en a besoin dans son `onSuccess`.
  const { mutate: publishPortal, isPending: isPortalPending } = usePortalPublishMutation(id ?? "");
  // Le rôle vente publie ses propres biens (décision produit 2026-07-12 :
  // plein pouvoir sur la fiche vente, publication comprise).
  const canTriggerPortals = ["admin", "c_level", "vente"].includes(role);
  /**
   * Dépublication (chantier 2026-09-14, feu vert Thibaud) : jusqu'ici,
   * repasser un bien Publié en Retiré/Brouillon ne notifiait JAMAIS Immoweb —
   * l'annonce restait live indéfiniment côté portail. Miroir exact du fix
   * fiche location (BienDetailPage.tsx) : à chaque sauvegarde réussie, si
   * `statutPublication` vient de franchir la frontière Publié/pas-Publié, on
   * répercute vers les portails externes activés.
   *
   * UNIQUEMENT le sens dépublication (`action === "deactivate"`) : le sens
   * inverse reste le bouton "Publier" dédié (`handlePublishClick` →
   * `markAsPublished` + boucle portails), qui pose aussi `last_published_at`
   * (un appel generique ici ne le fait pas). Déclencher les deux en même
   * temps (déroulant + bouton) envoyait deux requêtes quasi simultanées à
   * Immoweb, la seconde rejetée en 409 "REQUEST_CONFLICT" — reproduit en
   * direct côté fiche location le 2026-09-14. Décision publish/deactivate
   * dans `portalActionForStatutChange` (testé en isolation) — seule la
   * branche deactivate est utilisée ici.
   */
  const syncPortalOnStatutChange = (
    prevStatut: string | null | undefined,
    nextStatut: string | null | undefined,
  ) => {
    if (!canTriggerPortals) return;
    const action = portalActionForStatutChange(prevStatut, nextStatut);
    if (action !== "deactivate") return;
    for (const portal of externalEnabledPortals()) {
      publishPortal(
        { portal: portal.id, action },
        { onError: (e) => toast.error(`${portal.label} : ${e instanceof Error ? e.message : String(e)}`) },
      );
    }
  };

  /**
   * Mode « réglage des droits » — la page /v2/admin/droits-vente monte cette
   * fiche dans ce contexte. `null` en usage normal.
   */
  const permsEditor = useFieldPermsEditor();
  const permsMode = !!permsEditor;

  /**
   * ⚠️ GARDE-FOU DU MODE DROITS — ne pas contourner.
   *
   * Cette fiche s'enregistre au blur et toutes les 1,5 s. Montée en mode
   * réglage sur un bien réel, elle le MODIFIERAIT pendant qu'un admin règle
   * des permissions. On neutralise le point de passage unique plutôt que
   * chacun des quatre appelants — pour que ça reste vrai si un cinquième
   * apparaît. Même principe que sur la fiche location.
   */
  const mutateBien: typeof updateBien.mutate = (...args) => {
    if (permsMode) return;
    updateBien.mutate(...args);
  };

  // Brouillon local hydraté depuis la fiche chargée (ré-hydraté si l'id change
  // via la nav prev/next). Les strings restent potentiellement null en DB —
  // strVal() normalise à l'affichage, on ne mute jamais le draft en null.
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [hasEdited, setHasEdited] = useState(false);
  /** Dernier brouillon refusé par la base — garde-fou anti-boucle de l'autosave. */
  const lastRejectedDraft = useRef<Draft | null>(null);
  /**
   * Toujours le DERNIER `draft`, lisible depuis un `onSuccess` async (audit
   * 2026-09-10 §5 : sauver A, taper B pendant l'envoi, le succès de A
   * remettait `hasEdited=false` sans vérifier que B n'avait pas remplacé A
   * entre-temps — B restait affiché mais jamais persisté). Comparer le
   * brouillon capturé au moment de l'envoi à cette ref avant de blanchir
   * `hasEdited` : s'ils diffèrent, on laisse `hasEdited=true`, ce qui relance
   * automatiquement une sauvegarde dès que `updateBien.isPending` repasse à
   * `false` (l'effet ci-dessous en dépend déjà).
   */
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  useEffect(() => {
    if (bien && bien.id !== loadedId) {
      setDraft({ ...bien });
      setLoadedId(bien.id);
      setHasEdited(false);
    }
  }, [bien, loadedId]);

  // Onglet courant. Découpage par DESTINATAIRE de l'information (cf.
  // v2/lib/venteTabs) : le bâtiment, ce que l'acheteur inspecte, ce que le
  // notaire exige, ce qui se négocie, ce qui part chez l'acheteur.
  const [activeTab, setActiveTab] = useState<VenteTabId>("bien");
  const venteTabs = useMemo(
    () => VENTE_TAB_IDS.map((id) => ({ id, ...VENTE_TAB_META[id] })),
    [],
  );

  const OPTS = useMemo(() => getVenteOptions(t), [t]);

  // Suggestions projet (mêmes que la création)
  const { data: allBiens = [] } = useBiens();
  const projetSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const b of allBiens) {
      const v = (b.projet ?? "").trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v);
    }
    return out.sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [allBiens]);

  // Collaborateurs SRS (rôle collaborateur, actifs) — mêmes options que la
  // fiche location (retour user 2026-07-13 : même déroulant, même intitulé).
  const { data: profiles = [] } = useProfiles();
  const collaborateurOptions = useMemo(
    () =>
      profiles
        .filter((p) => p.role === "collaborateur" && p.statut !== "inactif")
        .map((p) => ({
          value: p.id,
          label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
        })),
    [profiles],
  );

  // Quartiers filtrés par le code postal saisi — même règle que la fiche
  // location : un quartier n'a de sens que dans sa commune.
  const { data: quartiersData = [] } = useQuartiers();
  const quartierOptions = useMemo(() => {
    const cp = typeof draft?.codePostal === "string" ? draft.codePostal : "";
    if (!cp) return [{ value: "", label: "—" }];
    return [
      { value: "", label: "—" },
      ...quartiersData
        .filter((q) => (q.codesPostaux ?? []).some((p) => p.code === cp))
        .map((q) => ({ value: q.id, label: q.nom ?? q.id })),
    ];
  }, [draft?.codePostal, quartiersData]);

  // ── Breadcrumb V2 (label + badge + nav prev/next du catalogue vente) ──────
  const nav = useListNavigation("biens-vente", id);
  const refLabel =
    (bien?.referenceInterne || bien?.reference || bien?.adresse || "").toString();
  // Montée hors de sa route (modale d'un workspace, éditeur de droits), la
  // fiche ne possède PAS le breadcrumb : sans ce garde, ouvrir la modale
  // détournerait le header de la page hôte (et son bouton Exporter).
  useBreadcrumbDetail(
    bien && !bienIdOverride
      ? {
          label: refLabel || t("vente.detail.title"),
          badge: <Badge variant="vente">{t("biens.transactionType.vente")}</Badge>,
          // Export PDF de la fiche (modèle docs/QP H1D1 FR.pdf) — vue print
          // dédiée hors layout, cf. BienVenteExportPage.
          actions: (
            <button
              type="button"
              onClick={() => navigate(`/v2/biens/${bien.id}/export-pdf`)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:text-accent hover:border-accent border border-border rounded-md cursor-pointer whitespace-nowrap transition-colors outline-none shadow-none"
            >
              <FileDown size={13} />
              {t("vente.export.button")}
            </button>
          ),
          navigation: {
            prevPath: nav.prevId ? `/biens/${nav.prevId}` : null,
            nextPath: nav.nextId ? `/biens/${nav.nextId}` : null,
            position: nav.position,
            total: nav.total,
          },
          // La fiche vente partage la route /v2/biens/:id avec la fiche
          // location : sans ça la sidebar surligne « Catalogue Location ».
          activeNavPath: "/v2/biens-vente",
        }
      : null,
    [bien?.id, refLabel, nav.prevId, nav.nextId, i18n.language],
  );

  // Tous les hooks sont posés — on peut retourner tôt.
  const data: Draft = draft ?? {};
  // `true` = inclure les critères de publication : sur la FICHE, un champ qui
  // bloque la mise en ligne doit être visible là où on le corrige.
  const hints = useMemo(() => getVenteMissingHints(data, true), [data]);
  const fields = useMemo(() => getVenteFormFields(data), [data]);
  // Pastilles rouges des onglets (cf. venteTabAlerts) — absentes jusqu'ici.
  const tabAlerts = useMemo(() => venteTabAlerts(hints, fields, VENTE_FICHE_PARKED), [hints, fields]);
  const prixM2Suggestion = useMemo(
    () => suggestPrixM2(data.prix, data.surfaceHabitable),
    [data.prix, data.surfaceHabitable],
  );

  // ── Persistance ───────────────────────────────────────────────────────────
  const buildPatch = () => {
    const patchInput: Draft = {};
    for (const [k, v] of Object.entries(data)) {
      if (v !== null) patchInput[k] = v;
    }
    return BienUpdateSchema.parse(patchInput);
  };

  /**
   * Auto-save DEBOUNCÉ, en plus du save au blur.
   *
   * Le blur seul ne suffit pas : les widgets custom ne renvoient pas de blur
   * exploitable au wrapper. Sur `SelectField`, cliquer une option la démonte
   * immédiatement (`setIsOpen(false)`) — or un élément focusé retiré du DOM
   * ne déclenche PAS de `focusout`. Résultat : changer « Statut transaction »
   * en « Vendu » modifiait le brouillon mais n'était jamais persisté, sauf si
   * l'utilisateur touchait ensuite un champ texte, dont le blur sauvait tout
   * le patch d'un coup (bug remonté par le PO le 2026-08-07).
   *
   * Idem pour les checkboxes, les répéteurs et la grille de proximité.
   *
   * La fiche location a ce filet depuis longtemps (BienDetailPage, même
   * commentaire) — seule la fiche vente était partie avec la moitié du
   * mécanisme. 1500 ms : assez long pour ne pas sauver à chaque frappe.
   */
  useEffect(() => {
    if (!hasEdited || updateBien.isPending) return;
    // Anti-boucle : en cas d'échec, `hasEdited` reste vrai et l'effet
    // relancerait le même patch toutes les 1,5 s — donc un toast d'erreur
    // toutes les 1,5 s depuis que useUpdateBien porte meta.toastOnError. On
    // mémorise le brouillon refusé : tant que l'utilisateur ne modifie rien,
    // on ne réessaie pas. La moindre édition crée un nouveau `draft` (autre
    // identité d'objet) et débloque la réessai.
    if (lastRejectedDraft.current === draft) return;
    const timer = setTimeout(() => {
      const attempted = draft;
      mutateBien(buildPatch(), {
        // Ne blanchir `hasEdited` que si rien n'a changé depuis l'envoi —
        // sinon l'édition la plus récente n'a jamais été sauvée (cf. ref ci-dessus).
        onSuccess: () => {
          if (attempted === draftRef.current) setHasEdited(false);
          syncPortalOnStatutChange(bien?.statutPublication, attempted?.statutPublication as string | undefined);
        },
        onError: () => {
          lastRejectedDraft.current = attempted;
        },
      });
    }, 1500);
    return () => clearTimeout(timer);
    // buildPatch capture `data` par closure : l'effet re-tourne à chaque
    // changement de draft, donc chaque timeout repart sur la version fraîche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, hasEdited, updateBien.isPending]);

  // toast / publishPortal / canTriggerPortals / syncPortalOnStatutChange sont
  // déclarés plus haut (juste après `updateBien`) : l'autosave débouncée plus
  // bas en a besoin dans son `onSuccess`, avant ce point du fichier.

  if (!bien || !draft) {
    return (
      <div className="py-16 text-center text-sm text-text-muted">
        {t("common.loading")}
      </div>
    );
  }

  // ── Setters (miroir de BienVenteCreateFormPage) ───────────────────────────
  const set = (key: string) => (value: string) => {
    setHasEdited(true);
    setDraft((prev) => ({ ...(prev ?? {}), [key]: value }));
  };
  // Date de disponibilité ⇒ déroulant « À une date précise » (cf. venteDispoDatePatch).
  const setDispoDate = (value: string) => {
    setHasEdited(true);
    setDraft((prev) => ({ ...(prev ?? {}), ...venteDispoDatePatch(value) }));
  };

  /**
   * Garde-fou client sur le passage en « Publié » — miroir exact du prédicat
   * serveur (`bien_publish_missing`, migration 082) et de ce que fait déjà la
   * fiche location. On refuse le changement et on nomme les critères
   * manquants, au lieu de laisser la base rejeter l'écriture en silence.
   */
  const onStatutPublicationChange = (next: string) => {
    if (data.statutPublication === next) return;
    if (next === "Publié") {
      const gate = isPublishable(data as unknown as PublishGateInput);
      if (!gate.ok) {
        toast.error(
          t("publishGate.blocked", {
            fields: gate.missing.map((k) => t(PUBLISH_GATE_LABEL_KEYS[k] ?? k)).join(", "),
          }),
        );
        return;
      }
    }
    set("statutPublication")(next);
  };

  // Édge function réelle depuis le 2026-09-12 (elle existait et fonctionnait
  // déjà côté serveur — testée manuellement le 2026-06-30 avec 27 succès
  // sandbox — mais rien côté UI ne l'appelait depuis la simplification du
  // bouton "Publier" du 2026-05-07). Uniquement après succès du site web :
  // pas la peine de pousser vers Immoweb un bien que `mark_bien_as_published`
  // vient de refuser.
  const handlePublishClick = () => {
    if (isMarkingPublished) return;
    markAsPublished(undefined, {
      onSuccess: () => {
        toast.success(t("portals.publishSuccessShort"));
        for (const portal of externalEnabledPortals()) {
          publishPortal(
            { portal: portal.id, action: "publish" },
            {
              onError: (e) =>
                toast.error(`${portal.label} : ${e instanceof Error ? e.message : String(e)}`),
            },
          );
        }
      },
      // Erreur remontée par le toast global (meta.toastOnError sur la mutation
      // n'existe pas ici) — on garde donc un onError local explicite.
      onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
    });
  };

  const setBoolFn = (key: string) => (s: string) => {
    setHasEdited(true);
    setDraft((prev) => ({
      ...(prev ?? {}),
      [key]: s === "oui" ? true : s === "non" ? false : null,
    }));
  };

  const boolToOption = (v: unknown): string =>
    v === true ? "oui" : v === false ? "non" : "";

  const strVal = (k: string): string => {
    const v = data[k];
    return typeof v === "string" ? v : "";
  };

  const handleCodePostalChange = (value: string) => {
    setHasEdited(true);
    setDraft((prev) => {
      const updated: Draft = { ...(prev ?? {}), codePostal: value };
      const city = getCityFromPostalCode(value);
      const region = getRegionFromPostalCode(value);
      if (city) updated.ville = city;
      if (region) updated.region = region;
      return updated;
    });
  };

  const handleVilleChange = (value: string) => {
    setHasEdited(true);
    setDraft((prev) => {
      const updated: Draft = { ...(prev ?? {}), ville: value };
      const code = getPostalCodeFromCity(value);
      if (code) {
        updated.codePostal = code;
        const region = getRegionFromPostalCode(code);
        if (region) updated.region = region;
      }
      return updated;
    });
  };

  const handleBlur = () => {
    if (!hasEdited || updateBien.isPending) return;
    // Même garde-fou que l'autosave débouncé : une frappe pendant l'envoi ne
    // doit pas être marquée "sauvée" par le succès de l'envoi précédent.
    const attempted = draft;
    mutateBien(buildPatch(), {
      onSuccess: () => {
        if (attempted === draftRef.current) setHasEdited(false);
        syncPortalOnStatutChange(bien?.statutPublication, attempted?.statutPublication as string | undefined);
      },
    });
  };

  // ── F : wrapper permissions (pattern BienDetailPage V1) ───────────────────
  /**
   * canSee=false → champ absent ; canEdit=false → champ VERROUILLÉ.
   *
   * Verrouillé, pas remplacé : on rend le même composant avec `readOnly`, donc
   * même boîte et même place dans la grille. Substituer un `ReadOnlyRow`
   * — une ligne de liste pleine largeur — faisait chevaucher libellés et
   * valeurs dès qu'un bloc mélangeait champs éditables et verrouillés (cf. le
   * commentaire détaillé sur le `F()` de BienDetailPage).
   */
  const F = (key: string, el: React.ReactElement | null) => {
    if (!canSee(key)) return null;
    if (!el) return el;
    if (!canEdit(key)) {
      return cloneElement(el, { readOnly: true } as Partial<{ readOnly: boolean }>);
    }
    return el;
  };

  // ── Repeaters terrasses / chambres ────────────────────────────────────────
  const rowsOf = (key: "terrasses" | "chambresDetail"): SurfaceOrientation[] => {
    const v = data[key];
    return Array.isArray(v) ? (v as SurfaceOrientation[]) : [];
  };

  // Miroirs (surfaceTerrasse, nbChambres, surfacesChambres) centralisés dans
  // `buildRepeaterPatch` — même logique que le formulaire de création vente et
  // que la fiche location. Corrige au passage l'absence de miroir
  // `surfacesChambres` : le répéteur n'alimentait que `chambresDetail`, or c'est
  // `surfacesChambres` que lit le mapper Immoweb — les surfaces d'un bien en
  // vente n'y arrivaient donc jamais.
  const setRows = (key: "terrasses" | "chambresDetail") => (next: SurfaceOrientation[]) => {
    setHasEdited(true);
    setDraft((prev) => ({ ...(prev ?? {}), ...buildRepeaterPatch(key, next) }) as Draft);
  };

  /**
   * Répéteur COMPACT partagé avec la fiche location (une cellule par entrée,
   * trois par ligne) au lieu de l'ancienne saisie pleine largeur : une surface
   * fait deux caractères, elle n'a jamais eu besoin d'une demi-largeur
   * d'écran.
   *
   * Les chambres sont PILOTÉES par `nbChambres`, saisi juste au-dessus dans la
   * même section : pas de bouton « Ajouter », et plus de saisie en double qui
   * pouvait contredire le compte. Les terrasses restent en mode libre — la
   * colonne `nbTerrasses` existe depuis la migration 104 mais n'est câblée sur
   * aucune des deux fiches à ce jour.
   */
  const renderRepeater = (key: "terrasses" | "chambresDetail") => {
    if (!canSee(key)) return null;
    return (
      <SurfaceOrientationRepeater
        kind={key === "terrasses" ? "terrasse" : "chambre"}
        rows={rowsOf(key)}
        onChange={setRows(key)}
        editable={canEdit(key)}
        count={key === "chambresDetail" ? Number(data.nbChambres) || 0 : undefined}
      />
    );
  };

  // ── Équipements ────────────────────────────────────────────────────────────
  const renderEquipements = () => {
    if (!canSee("equipements")) return null;
    const editable = canEdit("equipements");
    const current = Array.isArray(data.equipements) ? (data.equipements as string[]) : [];
    const toggleTag = (tag: string) => {
      setHasEdited(true);
      setDraft((prev) => {
        const cur = Array.isArray(prev?.equipements) ? (prev!.equipements as string[]) : [];
        return {
          ...(prev ?? {}),
          equipements: cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag],
        };
      });
    };
    // Quatre colonnes comme les équipements de la fiche location : neuf
    // libellés courts, tous lisibles d'un coup.
    return (
      <div
        className={cn(
          "grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3",
          !editable && "pointer-events-none select-none opacity-80",
        )}
      >
        {EQUIPEMENT_TAGS.map(({ tag, labelKey }) => (
          <Checkbox
            key={tag}
            label={t(labelKey)}
            checked={current.includes(tag)}
            onChange={() => toggleTag(tag)}
          />
        ))}
      </div>
    );
  };

  // ── À proximité ────────────────────────────────────────────────────────────
  const renderProximite = () => {
    if (!canSee("proximite")) return null;
    const editable = canEdit("proximite");
    const prox = (data.proximite ?? {}) as Proximite;
    const setEntry = (poi: string, patch: Partial<{ present: string; distance: string }>) => {
      setHasEdited(true);
      setDraft((prev) => {
        const cur = (prev?.proximite ?? {}) as Proximite;
        const entry = cur[poi as keyof Proximite] ?? { present: "", distance: "" };
        return { ...(prev ?? {}), proximite: { ...cur, [poi]: { ...entry, ...patch } } };
      });
    };
    // Même compaction que les répéteurs : un point d'intérêt = une cellule
    // (présence + distance sous la légende du POI), trois par ligne. Avant,
    // les six POI occupaient six lignes pleine largeur pour douze contrôles
    // dont la moitié tiennent en trois caractères — et « DISTANCE (M) » était
    // répété six fois.
    return (
      <div
        className={cn(
          "grid grid-cols-12 gap-x-4 gap-y-4",
          !editable && "pointer-events-none select-none opacity-80",
        )}
      >
        {PROXIMITE_POIS.map((poi) => {
          const entry = prox[poi] ?? { present: "", distance: "" };
          return (
            <div key={poi} className="col-span-12 sm:col-span-6 md:col-span-4">
              <FieldGroup
                legend={t(`vente.fields.proximite.${poi}`)}
                units={[
                  {
                    key: "present",
                    weight: 1.4,
                    node: (
                      <SelectField
                        label={t("vente.groups.proximitePresent")}
                        value={entry.present}
                        onChange={(v) => setEntry(poi, { present: v })}
                        options={OPTS.ouiNon}
                        labelBelow
                        align="center"
                      />
                    ),
                  },
                  {
                    key: "distance",
                    weight: 1,
                    node: (
                      <FormField
                        label={t("vente.groups.proximiteDistance")}
                        value={entry.distance}
                        onChange={(v) => setEntry(poi, { distance: v })}
                        placeholder={t("vente.form.distancePlaceholder")}
                        labelBelow
                        align="center"
                      />
                    ),
                  },
                ]}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // ── Galerie photos (upload storage → jsonb photos) ────────────────────────
  // Les fichiers partent au storage dès l'upload : on persiste la colonne
  // `photos` IMMÉDIATEMENT (sans attendre le blur) pour ne pas laisser de
  // fichiers orphelins si l'utilisateur quitte la page juste après.
  const renderPhotos = () => {
    if (!canSee("photos") || !id) return null;
    const current = Array.isArray(data.photos) ? (data.photos as BienPhoto[]) : [];
    return (
      <PhotoGallery
        bienId={id}
        value={current}
        onChange={(next) => {
          setDraft((prev) => ({ ...(prev ?? {}), photos: next }));
          setHasEdited(false);
          mutateBien({ ...buildPatch(), photos: next });
        }}
        readOnly={!canEdit("photos")}
      />
    );
  };

  // ── PDF certificat PEB (upload storage → colonne pebCertificatPdf) ────────
  // Comme les photos : le fichier part au storage dès le dépôt, on persiste
  // la colonne IMMÉDIATEMENT (sans attendre le blur) pour ne pas laisser de
  // fichier orphelin si l'utilisateur quitte la page juste après.
  const renderPebPdf = () => {
    if (!canSee("pebCertificatPdf") || !id) return null;
    return (
      <PebPdfDropzone
        bienId={id}
        value={strVal("pebCertificatPdf")}
        onChange={(url) => {
          setDraft((prev) => ({ ...(prev ?? {}), pebCertificatPdf: url }));
          setHasEdited(false);
          mutateBien({ ...buildPatch(), pebCertificatPdf: url });
        }}
        readOnly={!canEdit("pebCertificatPdf")}
      />
    );
  };

  // ── Détails multilingues ───────────────────────────────────────────────────
  const renderDetailsSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {VENTE_DETAILS_LANGS.map(({ titreKey, descriptionKey, cardLabelKey }) => (
        <div key={titreKey} className="bg-surface border border-border rounded-lg p-5">
          <h3 className="text-xs font-semibold tracking-widest uppercase text-text-secondary mb-4">
            {t(cardLabelKey)}
          </h3>
          <div className="flex flex-col gap-4">
            {F(
              titreKey,
              <FormField
                label={t("common.titleField")}
                value={strVal(titreKey)}
                onChange={set(titreKey)}
                maxLength={VENTE_TITLE_MAX}
              />,
            )}
            {F(
              descriptionKey,
              <TextAreaField
                label={t("common.description")}
                value={strVal(descriptionKey)}
                onChange={set(descriptionKey)}
                maxLength={VENTE_DESCRIPTION_MAX}
              />,
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // ── Rendu d'un champ depuis sa def (miroir création + permissions) ────────
  /**
   * Options de rendu posées par la GRILLE, pas par la def : dans un
   * `FieldGroup`, le libellé porteur est la légende, chaque case ne garde
   * qu'un micro-libellé sous elle. Props explicites plutôt qu'une classe
   * passée de l'extérieur — `cn` est un simple clsx sans tailwind-merge, on ne
   * peut pas écraser une classe depuis l'appelant.
   */
  interface FieldRenderOpts {
    /** Remplace le libellé long du champ (label de case dans un groupe). */
    label?: string;
    labelBelow?: boolean;
    align?: "left" | "center";
    /** Libellé À GAUCHE du contrôle — utilisé par la bande d'en-tête. */
    inlineLabel?: boolean;
  }

  const renderField = (def: VenteFieldDef, opts: FieldRenderOpts = {}) => {
    const hint = hints.get(def.key);
    const label = opts.label ?? t(def.labelKey ?? def.key);
    /**
     * Mode « réglage des droits » : on sort ici, au point de passage UNIQUE de
     * tous les champs de la fiche vente — y compris les cas spéciaux
     * (répéteurs, équipements, proximité, photos, PDF PEB) qui ne passent pas
     * par `F()`. Sur la fiche location il avait fallu huit interventions
     * séparées ; ici le `switch` centralise tout, donc une seule suffit.
     *
     * On sort AVANT les tests de permission : l'admin doit pouvoir rouvrir un
     * champ qu'il vient de passer à « caché ».
     */
    if (permsEditor) {
      return (
        <PermStateCell
          key={def.key}
          fieldKey={def.key}
          label={label}
          value={permsEditor.get(def.key)}
          onChange={(s) => permsEditor.set(def.key, s)}
          register={permsEditor.register}
          unregister={permsEditor.unregister}
          allowEdit={permsEditor.allowEdit ?? true}
        />
      );
    }
    // `labelBelow`/`align` ne sont pas acceptés par tous les composants — on ne
    // les transmet qu'à ceux qui les portent (FormField, SelectField, DateField).
    // `inlineLabel` n'existe que sur SelectField, d'où le champ à part.
    const boxed = { labelBelow: opts.labelBelow, align: opts.align };
    const inline = opts.inlineLabel ? { inlineLabel: true as const } : {};
    switch (def.kind) {
      case "chambres":
        return renderRepeater("chambresDetail");
      case "terrasses":
        return renderRepeater("terrasses");
      case "equipements":
        return renderEquipements();
      case "proximite":
        return renderProximite();
      case "photos":
        return renderPhotos();
      case "pebPdf":
        return renderPebPdf();
      case "collabSrs":
        return F(
          def.key,
          <SelectField
            key={def.key}
            label={label}
            value={strVal(def.key)}
            onChange={set(def.key)}
            options={collaborateurOptions}
            {...boxed}
          />,
        );
      case "quartier":
        // Lecture et écriture DIRECTES sur `quartier_id` (colonne DB, clé de
        // permission, présente dans le brouillon `{ ...bien }`). La fiche
        // location passe par un alias `quartier` avec deux passerelles
        // (init + patch) ; ici l'alias avait été repris sans les passerelles :
        // le déroulant restait vide et Zod strippait la clé inconnue à la
        // sauvegarde, donc aucun quartier n'arrivait en base (bug trouvé par
        // Thibaud 2026-09-16 sur AMP_EA2.1).
        return F(
          def.key,
          <SelectField
            key={def.key}
            label={label}
            value={strVal("quartier_id")}
            onChange={set("quartier_id")}
            options={quartierOptions}
            {...boxed}
          />,
        );
      case "freetext":
        return F(
          def.key,
          <FreeTextAutocompleteField
            key={def.key}
            label={label}
            value={strVal(def.key)}
            onChange={set(def.key)}
            suggestions={projetSuggestions}
            missingHint={hint}
          />,
        );
      case "select":
        return F(
          def.key,
          <SelectField
            key={def.key}
            label={label}
            value={strVal(def.key)}
            // `statutPublication` passe par le garde-fou client : c'est LE
            // geste de publication sur la fiche vente (il n'y a pas d'autre
            // chemin), donc il ne doit jamais échouer sans explication.
            onChange={def.key === "statutPublication" ? onStatutPublicationChange : set(def.key)}
            options={def.optionsId ? OPTS[def.optionsId] : []}
            missingHint={hint}
            {...boxed}
            {...inline}
          />,
        );
      case "boolSelect":
        return F(
          def.key,
          <SelectField
            key={def.key}
            label={label}
            value={boolToOption(data[def.key])}
            onChange={setBoolFn(def.key)}
            options={OPTS.ouiNon}
            missingHint={hint}
            {...boxed}
          />,
        );
      case "date":
        return F(
          def.key,
          <DateField
            key={def.key}
            label={label}
            value={strVal(def.key)}
            onChange={def.key === "venteDisponibiliteDate" ? setDispoDate : set(def.key)}
            missingHint={hint}
            {...boxed}
          />,
        );
      case "textarea":
        return F(
          def.key,
          <TextAreaField
            key={def.key}
            label={label}
            value={strVal(def.key)}
            onChange={set(def.key)}
            maxLength={def.maxLength}
          />,
        );
      case "checkbox":
        return F(
          def.key,
          // `items-end pb-2` : la case s'aligne sur le BAS des champs voisins
          // de la ligne, qui portent un libellé au-dessus d'eux.
          <div key={def.key} className="flex items-end h-full pb-2">
            <Checkbox
              label={label}
              checked={!!data[def.key]}
              onChange={(c) => {
                setHasEdited(true);
                if (def.key === "sousOption") {
                  // Même passerelle que la fiche location (sousOptionEnterPatch /
                  // sousOptionExitPatch) : le statut de publication change AUSSI
                  // côté client, donc l'autosave voit Publié → Retiré et envoie la
                  // dépublication à Immoweb. Avant, seule la case partait, le
                  // trigger SQL 084 passait le bien en Retiré en base et
                  // l'annonce restait en ligne (GP_C6.6, retour Thibaud 2026-09-16).
                  setDraft((prev) => {
                    const p = (prev ?? {}) as Record<string, unknown>;
                    const patch = c
                      ? sousOptionEnterPatch(
                          p.statutPublication as string | null | undefined,
                          todayIso(),
                          p.sousOptionDepuis as string | null | undefined,
                        )
                      : sousOptionExitPatch(p.sousOptionStatutAvant as string | null | undefined);
                    return { ...p, ...patch };
                  });
                  return;
                }
                setDraft((prev) => ({ ...(prev ?? {}), [def.key]: c }));
              }}
            />
          </div>,
        );
      case "text":
      default: {
        // Trois champs texte ont un comportement propre : code postal et
        // commune se déduisent l'un l'autre, le prix au m² suggère sa valeur
        // en placeholder.
        const onChangeText =
          def.key === "codePostal"
            ? handleCodePostalChange
            : def.key === "ville"
              ? handleVilleChange
              : set(def.key);
        // `placeholder` n'est pas destructuré par FormField : il part dans le
        // spread des props natives de l'input. Le passer à `undefined`
        // écraserait donc le « ... » par défaut du composant — on ne l'ajoute
        // que s'il a une valeur.
        const suggestion = def.key === "prixM2" ? prixM2Suggestion : "";
        return F(
          def.key,
          <FormField
            key={def.key}
            label={label}
            value={strVal(def.key)}
            onChange={onChangeText}
            {...(suggestion ? { placeholder: suggestion } : {})}
            maxLength={def.maxLength}
            missingHint={hint}
            {...boxed}
          />,
        );
      }
    }
  };

  // Champ(s) de la bande d'en-tête, retirés de leur bloc par `renderSection`.
  const headerField = fields
    .filter((f) => VENTE_FICHE_HEADER_FIELDS.has(f.key))
    .map((f) => renderField(f, { inlineLabel: true }))
    .filter(Boolean);

  // Bien recomposé pour les composants de publication : le brouillon local
  // (valeurs à l'écran) plus les horodatages qui, eux, ne viennent que de la
  // base.
  const publicationBien = {
    ...data,
    id: bien.id,
    updated_at: bien.updated_at,
    last_published_at: bien.last_published_at,
    content_updated_at: bien.content_updated_at,
  } as unknown as Bien;

  // ── Grille 12 colonnes d'une section ──────────────────────────────────────
  /** `renderField` + portée. `null` si le rôle ne voit pas le champ, pour ne
   *  pas laisser une cellule de grille vide derrière lui. */
  const cell = (def: VenteFieldDef, span: number, center?: boolean) => {
    const node = renderField(def, center ? { align: "center" } : {});
    if (!node) return null;
    return (
      <div key={def.key} className={SPAN_CLASS[span] ?? "col-span-12"}>
        {node}
      </div>
    );
  };

  /**
   * Rend une section : un bloc `SectionCard` compact, pleine largeur, dont le
   * contenu suit la grille déclarée dans `venteFicheLayout`.
   *
   * Les champs non placés par le layout sont rendus en fin de section avec une
   * portée par défaut plutôt que disparaître — le test `venteFicheLayout.test`
   * les signale à la CI.
   */
  const renderSection = (section: VenteSection) => {
    if (section === "details") return renderDetailsSection();

    // Retirés du bloc : ce qui monte dans la bande d'en-tête, et les champs
    // volontairement mis de côté en attendant qu'on leur choisisse une place.
    const sectionFields = fields.filter(
      (f) =>
        f.section === section &&
        !VENTE_FICHE_HEADER_FIELDS.has(f.key) &&
        !VENTE_FICHE_PARKED.has(f.key),
    );
    if (sectionFields.length === 0) return null;
    const defOf = new Map(sectionFields.map((f) => [f.key, f]));
    const placed = new Set(venteLayoutKeys(section));

    const items = VENTE_SECTION_LAYOUT[section].flatMap((item) => {
      if (item.kind === "block") {
        const def = defOf.get(item.key);
        if (!def) return [];
        const node = renderField(def);
        if (!node) return [];
        return [
          <div
            key={item.key}
            className={cn("col-span-12", item.titleKey && "border-t border-border pt-4 mt-1")}
          >
            {item.titleKey && (
              <p className="text-[10.5px] font-semibold tracking-widest uppercase text-text-secondary mb-3">
                {t(item.titleKey)}
              </p>
            )}
            {node}
          </div>,
        ];
      }
      if (item.kind === "group") {
        const units: FieldGroupUnit[] = item.units.flatMap((u) => {
          const def = defOf.get(u.key);
          // Absent = masqué par une condition (n° d'annexe sans « disponible »)
          // ou inconnu. Le FieldGroup se referme tout seul sur les cases qui
          // restent.
          if (!def) return [];
          return [
            {
              key: u.key,
              weight: u.weight,
              basis: u.basis,
              before: u.beforeKey ? t(u.beforeKey) : undefined,
              node: renderField(def, {
                label: t(u.labelKey),
                labelBelow: true,
                align: u.center ? "center" : undefined,
              }),
            },
          ];
        });
        if (units.length === 0) return [];
        return [
          <div key={item.legendKey} className={SPAN_CLASS[item.span] ?? "col-span-12"}>
            <FieldGroup legend={t(item.legendKey)} units={units} />
          </div>,
        ];
      }
      const def = defOf.get(item.key);
      if (!def) return [];
      const node = cell(def, item.span, item.center);
      return node ? [node] : [];
    });

    const orphelins = sectionFields
      .filter((f) => !placed.has(f.key))
      .map((f) => cell(f, VENTE_DEFAULT_SPAN))
      .filter(Boolean);

    const publishExtras =
      section === "publication" && canTriggerPortals
        ? [
            // Parité avec la fiche location : bouton « Publier » (pousse le
            // contenu vers les portails / le site public) + bannière
            // « modifications non poussées ». Le bouton reste grisé tant que le
            // bien n'est pas Publié ET modifié depuis le dernier envoi — c'est
            // `getPublicationStatus` qui l'arbitre.
            <div key="publish" className="col-span-12 border-t border-border pt-4 mt-1 flex flex-col gap-3">
              <PublicationPublishButton
                bien={publicationBien}
                onPublishClick={handlePublishClick}
                isPending={isMarkingPublished}
              />
              {/* Statut Immoweb réel (portal_publications) — demande Thibaud
                  2026-09-16 : on ne voyait pas un REJECTED sans aller en base. */}
              <ImmowebStatusLine
                bienId={publicationBien.id}
                codeImmoweb={publicationBien.codeImmoweb}
                statutPublication={publicationBien.statutPublication}
                deactivating={isPortalPending}
                onDeactivate={() =>
                  publishPortal(
                    { portal: "immoweb", action: "deactivate" },
                    { onError: (e) => toast.error(`Immoweb : ${e instanceof Error ? e.message : String(e)}`) },
                  )
                }
              />
              <PublicationStaleBanner bien={publicationBien} />
            </div>,
          ]
        : [];

    const content = [...items, ...orphelins, ...publishExtras];
    if (content.length === 0) return null;

    return (
      <SectionCard
        title={t(`vente.form.sections.${section}`)}
        icon={VENTE_SECTION_ICON[section]}
        columns={1}
        collapsible
        density="compact"
      >
        <div className={GRID12}>{content}</div>
      </SectionCard>
    );
  };

  // Critères de publication manquants — même dispositif visuel que la fiche
  // location (bandeau rouge en tête + bordures rouges sur les champs, cf.
  // getVenteMissingHints). Le toast d'erreur seul ne disait pas OÙ corriger.
  // `sousOption` est écarté : c'est un état DÉLIBÉRÉ (négociation en cours),
  // pas un champ à remplir. Le lister comme « critère manquant » induirait en
  // erreur — la case et son badge le signalent déjà.
  const publishMissing = isPublishable(data as unknown as PublishGateInput).missing.filter(
    (k) => k !== "sousOption",
  );

  return (
    // Pas de `max-w-*` ici : la fiche occupe toute la zone de contenu, comme la
    // fiche location et comme les catalogues. Le `max-w-5xl mx-auto` d'origine
    // plafonnait à 1024 px et centrait, ce qui creusait 60 px de marge de
    // chaque côté contre 24 partout ailleurs — l'écart au menu de gauche ne
    // disait plus la même chose d'un écran à l'autre.
    <div>
      <div className="space-y-5" onBlur={handleBlur}>
        {/* Masqué en mode droits : ce bandeau parle de la complétude du bien
            qui sert de support d'affichage, pas des permissions qu'on règle. */}
        {!permsMode && publishMissing.length > 0 && (
          <div className="rounded-md bg-danger/10 border border-danger/40 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-danger">
                  {t("publishGate.bannerTitle", { count: publishMissing.length })}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {t("publishGate.bannerHint")}
                </p>
                <ul className="text-xs text-text-primary mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {publishMissing.map((k) => (
                    <li key={k} className="before:content-['•'] before:mr-1 before:text-danger">
                      {t(PUBLISH_GATE_LABEL_KEYS[k] ?? k)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        {/* Bande d'en-tête — HORS onglets (demande Thibaud 2026-08-09, même
            principe que la fiche location). Le statut de la transaction se lit
            en permanence : il ne doit pas dépendre de l'onglet ouvert. Pas de
            badge d'étape ici, contrairement à la location : `getLifecycleStage`
            décrit le cycle ELS d'une rotation locative, une vente n'en a pas.
            Libellé à gauche du contrôle pour garder la ligne compacte. */}
        {headerField.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3 pb-4 border-b border-border">
            {headerField}
          </div>
        )}
        {/* Barre d'onglets — sous le bandeau des critères manquants, qui
            concerne la fiche entière et non l'onglet courant (même ordre que
            la fiche location). */}
        <FicheTabBar tabs={venteTabs} active={activeTab} onChange={setActiveTab} alerts={tabAlerts} />

        {/* Les blocs sont EMPILÉS EN PLEINE LARGEUR, jamais appariés deux par
            deux : c'est l'appariement qui produisait le vide côté location
            (une grille aligne ses cellules sur la plus haute). */}
        {VENTE_FICHE_SECTION_ORDER.filter((sec) => VENTE_TAB_OF_SECTION[sec] === activeTab).map((section) => {
          const node = renderSection(section);
          // `null` et non un div vide : `space-y-5` ajouterait sinon 20 px de
          // vide pour une section que personne ne voit.
          return node ? <div key={section}>{node}</div> : null;
        })}
      </div>
    </div>
  );
}
